/**
 * TrainerVisibilityCard
 * Antrenörün ana sayfada liste görünürlüğünü yönetir (KVKK uyumlu)
 * 
 * Durumlar:
 * - none/null: Hiç talep göndermemiş → "t("trainers:showOnList")" butonu
 * - pending: Talep bekliyor → "{t("trainers:requestSent")}" (disabled)
 * - approved: Onaylandı → ON/OFF toggle (istediği zaman açıp kapatabilir)
 * - rejected: Reddedildi → "{t("trainers:resendRequest")}" butonu
 * - removed: Admin kaldırdı → "Yeniden {t("trainers:sendRequest")}" butonu
 */
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Eye, 
  EyeOff, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  Shield,
  Send
} from "lucide-react";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrainerVisibilityCard({ trainerId, isOwnProfile }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [loading, setLoading] = useState(true);
  const [visibilityData, setVisibilityData] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchVisibilityStatus = async () => {
    try {
      const res = await fetch(`${API}/api/trainers/${trainerId}/visibility-status`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setVisibilityData(data);
      }
    } catch (error) {
      console.error("Error fetching visibility status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trainerId && isOwnProfile) {
      fetchVisibilityStatus();
    } else {
      setLoading(false);
    }
  }, [trainerId, isOwnProfile]);

  const handleRequestVisibility = async () => {
    if (!kvkkConsent) {
      toast.error(t("admin:toast_kvkkOnayiZorunludur"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/trainers/${trainerId}/visibility-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kvkk_consent: true })
      });

      if (res.ok) {
        toast.success(t("admin:toast_talebinizGonderildiAdminOnayindanSonra"));
        setShowRequestModal(false);
        setKvkkConsent(false);
        fetchVisibilityStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("common:requestSendFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle visibility ON/OFF (only for approved status)
  const handleToggleVisibility = async (newValue) => {
    setToggling(true);
    try {
      const res = await fetch(`${API}/api/trainers/${trainerId}/visibility-toggle`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_visible: newValue })
      });

      if (res.ok) {
        toast.success(newValue ? t("common:visibleOnList") : t("common:hiddenFromList"));
        fetchVisibilityStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setToggling(false);
    }
  };

  // Only show for own profile
  if (!isOwnProfile) return null;

  if (loading) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-navy" />
        </CardContent>
      </Card>
    );
  }

  const status = visibilityData?.visibility_status || "none";
  const isVisible = visibilityData?.is_publicly_visible || false;

  // Render different UI based on status
  const renderContent = () => {
    switch (status) {
      case "approved":
        // Onaylanmış - ON/OFF toggle göster
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isVisible ? (
                  <Eye className="w-5 h-5 text-green-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-slate-500" />
                )}
                <div>
                  <Badge className={isVisible ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                    {isVisible ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> {t("trainers:visibleOnListLabel")}</>
                    ) : (
                      <><EyeOff className="w-3 h-3 mr-1" /> Gizli</>
                    )}
                  </Badge>
                  <p className="text-xs text-grey mt-1">
                    Onaylandı: {visibilityData?.visibility_approved_date 
                      ? format(new Date(visibilityData.visibility_approved_date), "dd/MM/yyyy HH:mm", { locale: getDateLocale() }) 
                      : "-"}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {toggling && <Loader2 className="w-4 h-4 animate-spin text-navy" />}
                <Switch
                  checked={isVisible}
                  onCheckedChange={handleToggleVisibility}
                  disabled={toggling}
                />
              </div>
            </div>
            
            <p className="text-xs text-grey bg-slate-50 p-3 rounded-lg">
              {isVisible 
                ? t("common:visibilityProfileDesc")
                : t("admin:visibilityClosedDesc")}
            </p>
          </div>
        );

      case "pending":
        // Onay bekliyor
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <div>
                <Badge className="bg-amber-100 text-amber-700">
                  <Send className="w-3 h-3 mr-1" /> Talep Gönderildi
                </Badge>
                <p className="text-xs text-grey mt-1">{t("admin:adminOnayiBekleniyor")}</p>
              </div>
            </div>
            
            <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg">
              {t("trainers:requestUnderReview")}
            </p>
          </div>
        );

      case "rejected":
        // Reddedildi
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <Badge className="bg-red-100 text-red-700">
                    <XCircle className="w-3 h-3 mr-1" /> Reddedildi
                  </Badge>
                  {visibilityData?.rejection_reason && (
                    <p className="text-xs text-red-600 mt-1">Neden: {visibilityData.rejection_reason}</p>
                  )}
                </div>
              </div>
              
              <Button 
                onClick={() => setShowRequestModal(true)}
                variant="outline"
                className="border-navy text-navy hover:bg-navy/5"
              >
                <Eye className="w-4 h-4 mr-2" />
                Yeniden Talep Et
              </Button>
            </div>
          </div>
        );

      case "removed":
        // Admin tarafından kaldırıldı
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-slate-600" />
                <div>
                  <Badge className="bg-slate-100 text-slate-700">
                    <EyeOff className="w-3 h-3 mr-1" /> Admin Tarafından Kaldırıldı
                  </Badge>
                  <p className="text-xs text-grey mt-1">
                    Kaldırıldı: {visibilityData?.visibility_removed_date 
                      ? format(new Date(visibilityData.visibility_removed_date), "dd/MM/yyyy HH:mm", { locale: getDateLocale() }) 
                      : "-"}
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowRequestModal(true)}
                className="bg-navy hover:bg-navy/90"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t("trainers:resendRequest")}
              </Button>
            </div>
            
            <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg">
              Listede görünmek için yeniden talep göndermeniz ve admin onayı almanız gerekmektedir.
            </p>
          </div>
        );

      default:
        // Hiç talep göndermemiş
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EyeOff className="w-5 h-5 text-grey" />
                <div>
                  <Badge variant="secondary">
                    <EyeOff className="w-3 h-3 mr-1" /> {t("trainers:notVisibleOnList")}
                  </Badge>
                  <p className="text-xs text-grey mt-1">{t("common:anaSayfadakiAntrenorListesindeGorunmuyor")}</p>
                </div>
              </div>
              
              <Button 
                onClick={() => setShowRequestModal(true)}
                className="bg-navy hover:bg-navy/90"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t("trainers:showOnList")}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {status === "approved" && isVisible ? (
              <Eye className="w-5 h-5 text-green-600" />
            ) : (
              <EyeOff className="w-5 h-5 text-grey" />
            )}
            {t("trainers:homepageVisibility")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>

      {/* KVKK Onay Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-navy" />
              {t("trainers:visibilityRequestTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("trainers:kvkkConsentRequired")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-xl text-sm text-grey">
              <h4 className="font-medium text-navy mb-2">{t("trainers:visibleInfoTitle")}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('common:fullName')}</li>
                <li>{t("trainers:profilePhotoInfo")}</li>
                <li>{t('common:branches')}</li>
                <li>{t("trainers:businessInfo")}</li>
                <li>{t("trainers:ratingInfo")}</li>
              </ul>
            </div>

            <div className="flex items-start gap-3 p-4 border rounded-xl">
              <Checkbox
                id="kvkk-consent"
                checked={kvkkConsent}
                onCheckedChange={setKvkkConsent}
                className="mt-1"
              />
              <label htmlFor="kvkk-consent" className="text-sm cursor-pointer">
                <span className="font-medium text-navy">{t("admin:kvkkOnayi")}</span>
                <p className="text-grey mt-1">
                  {t("trainers:kvkkConsentText")}
                </p>
              </label>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-lg">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t("trainers:requestAdminApproval")}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>{t('common:cancel')}</Button>
            <Button 
              onClick={handleRequestVisibility}
              disabled={!kvkkConsent || submitting}
              className="bg-navy hover:bg-navy/90"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("trainers:sendRequest")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
